import { useState } from 'react';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Palette, Package, Users, Settings, Plus, Trash2, Edit2, Image, User } from 'lucide-react';

const Admin = () => {
  const { t } = useI18n();
  const { user, isAdmin, loading } = useAuth();

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" /></div>;
  if (!user || !isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen pt-16">
      <div className="container mx-auto px-4 py-6">
        <h1 className="font-display text-xl md:text-2xl font-bold text-foreground mb-4">{t('adminDashboard')}</h1>
        <AdminStats />
        <Tabs defaultValue="artworks" className="mt-6">
          <TabsList className="bg-card border border-border/30 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="artworks" className="gap-1.5 text-xs data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
              <Palette className="h-3.5 w-3.5" /> {t('manageArtworks')}
            </TabsTrigger>
            <TabsTrigger value="orders" className="gap-1.5 text-xs data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
              <Package className="h-3.5 w-3.5" /> {t('manageOrders')}
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-1.5 text-xs data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
              <Users className="h-3.5 w-3.5" /> {t('manageUsers')}
            </TabsTrigger>
            <TabsTrigger value="artist" className="gap-1.5 text-xs data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
              <User className="h-3.5 w-3.5" /> {t('aboutArtist')}
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-1.5 text-xs data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
              <Settings className="h-3.5 w-3.5" /> {t('settings')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="artworks"><ArtworksManager /></TabsContent>
          <TabsContent value="orders"><OrdersManager /></TabsContent>
          <TabsContent value="users"><UsersManager /></TabsContent>
          <TabsContent value="artist"><ArtistManager /></TabsContent>
          <TabsContent value="settings"><SettingsManager /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const AdminStats = () => {
  const { t } = useI18n();
  const { data: stats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const [artworks, orders, users] = await Promise.all([
        supabase.from('artworks').select('id', { count: 'exact', head: true }),
        supabase.from('orders').select('id, total_price, status'),
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
      ]);
      const allOrders = orders.data || [];
      const revenue = allOrders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + Number(o.total_price), 0);
      const pending = allOrders.filter(o => o.status === 'pending').length;
      const confirmed = allOrders.filter(o => ['confirmed', 'payment_confirmed'].includes(o.status)).length;
      return { artworks: artworks.count || 0, orders: allOrders.length, users: users.count || 0, revenue, pending, confirmed };
    },
  });

  const items = [
    { label: t('totalArtworks'), value: stats?.artworks || 0 },
    { label: t('totalOrders'), value: stats?.orders || 0 },
    { label: t('pending'), value: stats?.pending || 0 },
    { label: t('revenue'), value: `$${stats?.revenue || 0}` },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {items.map(({ label, value }) => (
        <div key={label} className="bg-card rounded-xl p-4 border border-border/20 shadow-soft">
          <p className="text-xl md:text-2xl font-bold text-foreground">{value}</p>
          <p className="text-[10px] text-muted-foreground mt-1">{label}</p>
        </div>
      ))}
    </div>
  );
};

const ArtworksManager = () => {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({ title: '', title_ar: '', description: '', description_ar: '', price: '', category: 'flowers', image_url: '', featured: false });
  const [extraImages, setExtraImages] = useState<string[]>([]);

  const { data: artworks = [] } = useQuery({
    queryKey: ['admin-artworks'],
    queryFn: async () => {
      const { data } = await supabase.from('artworks').select('*').order('created_at', { ascending: false });
      return data || [];
    },
  });

  const uploadImage = async (file: File): Promise<string> => {
    const ext = file.name.split('.').pop();
    const path = `${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('artworks').upload(path, file);
    if (error) throw error;
    const { data } = supabase.storage.from('artworks').getPublicUrl(path);
    return data.publicUrl;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title, title_ar: form.title_ar || null,
        description: form.description || null, description_ar: form.description_ar || null,
        price: parseFloat(form.price) || 0, category: form.category,
        image_url: form.image_url || null, featured: form.featured,
      };
      let artworkId: string;
      if (editing) {
        const { error } = await supabase.from('artworks').update(payload).eq('id', editing.id);
        if (error) throw error;
        artworkId = editing.id;
        // Delete old extra images
        await supabase.from('artwork_images').delete().eq('artwork_id', artworkId);
      } else {
        const { data, error } = await supabase.from('artworks').insert(payload).select('id').single();
        if (error) throw error;
        artworkId = data.id;
      }
      // Insert extra images
      if (extraImages.length > 0) {
        const rows = extraImages.map((url, i) => ({ artwork_id: artworkId, image_url: url, display_order: i + 1 }));
        await supabase.from('artwork_images').insert(rows);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-artworks'] });
      queryClient.invalidateQueries({ queryKey: ['artworks'] });
      toast.success(t('success'));
      resetForm();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('artworks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-artworks'] });
      toast.success(t('success'));
    },
  });

  const resetForm = () => {
    setForm({ title: '', title_ar: '', description: '', description_ar: '', price: '', category: 'flowers', image_url: '', featured: false });
    setExtraImages([]);
    setEditing(null);
    setShowForm(false);
  };

  const startEdit = async (artwork: any) => {
    setForm({
      title: artwork.title, title_ar: artwork.title_ar || '',
      description: artwork.description || '', description_ar: artwork.description_ar || '',
      price: String(artwork.price), category: artwork.category,
      image_url: artwork.image_url || '', featured: artwork.featured || false,
    });
    const { data } = await supabase.from('artwork_images').select('image_url').eq('artwork_id', artwork.id).order('display_order');
    setExtraImages((data || []).map(d => d.image_url));
    setEditing(artwork);
    setShowForm(true);
  };

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display text-base font-semibold text-foreground">{t('manageArtworks')}</h2>
        <Button onClick={() => { resetForm(); setShowForm(true); }} size="sm" className="gradient-accent text-primary-foreground text-xs">
          <Plus className="h-3.5 w-3.5 mr-1" /> {t('addArtwork')}
        </Button>
      </div>

      {showForm && (
        <div className="bg-card rounded-xl p-5 border border-border/20 mb-6 animate-fade-in-up">
          <form onSubmit={e => { e.preventDefault(); saveMutation.mutate(); }} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs">{t('title')} (EN)</Label><Input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="bg-background border-border/30 h-9 text-sm" /></div>
            <div><Label className="text-xs">{t('title')} (AR)</Label><Input value={form.title_ar} onChange={e => setForm(f => ({ ...f, title_ar: e.target.value }))} className="bg-background border-border/30 h-9 text-sm" dir="rtl" /></div>
            <div><Label className="text-xs">{t('description')} (EN)</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="bg-background border-border/30 text-sm" rows={2} /></div>
            <div><Label className="text-xs">{t('description')} (AR)</Label><Textarea value={form.description_ar} onChange={e => setForm(f => ({ ...f, description_ar: e.target.value }))} className="bg-background border-border/30 text-sm" rows={2} dir="rtl" /></div>
            <div><Label className="text-xs">{t('price')}</Label><Input required type="number" step="0.01" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="bg-background border-border/30 h-9 text-sm" /></div>
            <div><Label className="text-xs">{t('category')}</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="bg-background border-border/30 h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['flowers', 'landscapes', 'fantasy', 'animals', 'abstract'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Main image */}
            <div className="md:col-span-2">
              <Label className="text-xs">Main Image</Label>
              <div className="flex gap-2">
                <Input placeholder="Image URL" value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} className="bg-background border-border/30 flex-1 h-9 text-sm" />
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    try { const url = await uploadImage(file); setForm(f => ({ ...f, image_url: url })); toast.success('Uploaded!'); } catch (err: any) { toast.error(err.message); }
                  }} />
                  <div className="h-9 px-3 rounded-md border border-border/30 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
                    <Image className="h-3.5 w-3.5" /> Upload
                  </div>
                </label>
              </div>
            </div>
            {/* Extra images */}
            <div className="md:col-span-2">
              <Label className="text-xs">Additional Images</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {extraImages.map((url, i) => (
                  <div key={i} className="relative w-16 h-16">
                    <img src={url} className="w-full h-full object-cover rounded-lg border border-border/30" />
                    <button type="button" onClick={() => setExtraImages(prev => prev.filter((_, j) => j !== i))}
                      className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-[8px]">×</button>
                  </div>
                ))}
                <label className="cursor-pointer w-16 h-16 rounded-lg border-2 border-dashed border-border/30 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/30 transition-colors">
                  <input type="file" accept="image/*" multiple className="hidden" onChange={async (e) => {
                    const files = e.target.files; if (!files) return;
                    for (const file of Array.from(files)) {
                      try { const url = await uploadImage(file); setExtraImages(prev => [...prev, url]); } catch (err: any) { toast.error(err.message); }
                    }
                  }} />
                  <Plus className="h-5 w-5" />
                </label>
              </div>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))} className="rounded border-border" />
              <Label className="text-xs">Featured</Label>
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit" disabled={saveMutation.isPending} size="sm" className="gradient-accent text-primary-foreground text-xs">{t('save')}</Button>
              <Button type="button" variant="outline" onClick={resetForm} size="sm" className="border-border/30 text-xs">{t('cancel')}</Button>
            </div>
          </form>
        </div>
      )}

      <div className="grid gap-2">
        {artworks.map(artwork => (
          <div key={artwork.id} className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border/20">
            {artwork.image_url && <img src={artwork.image_url} alt={artwork.title} className="w-12 h-12 object-cover rounded-lg flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground text-sm truncate">{artwork.title}</p>
              <p className="text-xs text-muted-foreground capitalize">{artwork.category} • ${artwork.price}</p>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(artwork)}><Edit2 className="h-3.5 w-3.5 text-muted-foreground" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteMutation.mutate(artwork.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OrdersManager = () => {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const { data: orders = [] } = useQuery({
    queryKey: ['admin-orders'],
    queryFn: async () => {
      const { data } = await supabase.from('orders').select('*, artworks(title)').order('created_at', { ascending: false });
      return data || [];
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('orders').update({ status }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
      toast.success(t('success'));
    },
  });

  const statusColors: Record<string, string> = {
    pending: 'bg-gold/20',
    confirmed: 'bg-soft-green/30',
    payment_confirmed: 'bg-primary/15',
    shipped: 'bg-primary/10',
    delivered: 'bg-soft-green/50',
    cancelled: 'bg-destructive/10',
  };

  return (
    <div className="mt-4 space-y-2">
      {orders.length === 0 ? (
        <p className="text-center text-muted-foreground py-10 text-sm">{t('noArtworks')}</p>
      ) : orders.map((order: any) => (
        <div key={order.id} className={`p-4 rounded-xl border border-border/20 ${statusColors[order.status] || 'bg-card'}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground text-sm">{order.customer_name}</p>
              <p className="text-xs text-muted-foreground">{order.artworks?.title || 'N/A'} • ${order.total_price}</p>
              <p className="text-[10px] text-muted-foreground mt-1">📍 {order.address} • 📞 {order.phone}</p>
              {order.notes && <p className="text-[10px] text-muted-foreground">📝 {order.notes}</p>}
              <p className="text-[10px] text-muted-foreground">{new Date(order.created_at).toLocaleString()}</p>
            </div>
            <div className="flex flex-col gap-1.5 items-end">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-card border border-border/30">
                {order.status}
              </span>
              <Select value={order.status} onValueChange={v => updateStatus.mutate({ id: order.id, status: v })}>
                <SelectTrigger className="w-36 bg-card border-border/30 text-xs h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">{t('pending')}</SelectItem>
                  <SelectItem value="confirmed">{t('confirmed')}</SelectItem>
                  <SelectItem value="payment_confirmed">{t('paymentConfirmed')}</SelectItem>
                  <SelectItem value="shipped">{t('shipped')}</SelectItem>
                  <SelectItem value="delivered">{t('delivered')}</SelectItem>
                  <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const UsersManager = () => {
  const { data: users = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data: profiles } = await supabase.from('profiles').select('*');
      const { data: roles } = await supabase.from('user_roles').select('*');
      return (profiles || []).map(p => ({
        ...p,
        roles: (roles || []).filter(r => r.user_id === p.user_id).map(r => r.role),
      }));
    },
  });

  return (
    <div className="mt-4 space-y-2">
      {users.map((user: any) => (
        <div key={user.id} className="flex items-center justify-between p-3 bg-card rounded-lg border border-border/20">
          <div>
            <p className="font-medium text-foreground text-sm">{user.display_name || 'Unknown'}</p>
            <p className="text-[10px] text-muted-foreground">{user.roles?.includes('admin') ? '👑 Admin' : '👤 Customer'}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const ArtistManager = () => {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ artist_name: '', artist_name_ar: '', artist_bio: '', artist_bio_ar: '', artist_image: '', background_image: '' });
  const [loaded, setLoaded] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('*');
      return data || [];
    },
  });

  if (settings && !loaded) {
    const map: Record<string, string> = {};
    settings.forEach(s => { map[s.key] = s.value; });
    setForm({
      artist_name: map.artist_name || '', artist_name_ar: map.artist_name_ar || '',
      artist_bio: map.artist_bio || '', artist_bio_ar: map.artist_bio_ar || '',
      artist_image: map.artist_image || '',
      background_image: map.background_image || '',
    });
    setLoaded(true);
  }

  const uploadImage = async (file: File): Promise<string> => {
    const ext = file.name.split('.').pop();
    const path = `artist_${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('artworks').upload(path, file);
    if (error) throw error;
    const { data } = supabase.storage.from('artworks').getPublicUrl(path);
    return data.publicUrl;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const [key, value] of Object.entries(form)) {
        const existing = settings?.find(s => s.key === key);
        if (existing) {
          await supabase.from('site_settings').update({ value }).eq('key', key);
        } else {
          await supabase.from('site_settings').insert({ key, value });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast.success(t('success'));
    },
  });

  return (
    <div className="mt-4 max-w-lg">
      <div className="bg-card rounded-xl p-5 border border-border/20">
        <h3 className="font-display text-base font-semibold text-foreground mb-4">{t('aboutArtist')}</h3>
        <form onSubmit={e => { e.preventDefault(); saveMutation.mutate(); }} className="space-y-3">
          <div><Label className="text-xs">Name (EN)</Label><Input value={form.artist_name} onChange={e => setForm(f => ({ ...f, artist_name: e.target.value }))} className="bg-background border-border/30 h-9 text-sm" /></div>
          <div><Label className="text-xs">Name (AR)</Label><Input value={form.artist_name_ar} onChange={e => setForm(f => ({ ...f, artist_name_ar: e.target.value }))} className="bg-background border-border/30 h-9 text-sm" dir="rtl" /></div>
          <div><Label className="text-xs">Bio (EN)</Label><Textarea value={form.artist_bio} onChange={e => setForm(f => ({ ...f, artist_bio: e.target.value }))} className="bg-background border-border/30 text-sm" rows={4} /></div>
          <div><Label className="text-xs">Bio (AR)</Label><Textarea value={form.artist_bio_ar} onChange={e => setForm(f => ({ ...f, artist_bio_ar: e.target.value }))} className="bg-background border-border/30 text-sm" rows={4} dir="rtl" /></div>
          <div>
            <Label className="text-xs">Artist Photo</Label>
            <div className="flex gap-2 items-center">
              {form.artist_image && <img src={form.artist_image} className="w-12 h-12 rounded-full object-cover" />}
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  try { const url = await uploadImage(file); setForm(f => ({ ...f, artist_image: url })); } catch (err: any) { toast.error(err.message); }
                }} />
                <div className="h-9 px-3 rounded-md border border-border/30 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
                  <Image className="h-3.5 w-3.5" /> Upload
                </div>
              </label>
            </div>
          </div>
          <div>
            <Label className="text-xs">Background Image</Label>
            <div className="flex gap-2 items-center">
              {form.background_image && <div className="w-12 h-12 rounded border border-border/30 overflow-hidden"><img src={form.background_image} className="w-full h-full object-cover" /></div>}
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  try { const url = await uploadImage(file); setForm(f => ({ ...f, background_image: url })); } catch (err: any) { toast.error(err.message); }
                }} />
                <div className="h-9 px-3 rounded-md border border-border/30 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
                  <Image className="h-3.5 w-3.5" /> Upload
                </div>
              </label>
            </div>
          </div>
          <Button type="submit" disabled={saveMutation.isPending} size="sm" className="gradient-accent text-primary-foreground text-xs">{t('save')}</Button>
        </form>
      </div>
    </div>
  );
};

const SettingsManager = () => {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [whatsapp, setWhatsapp] = useState('');

  const { data: settings } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('*');
      return data || [];
    },
  });

  const currentWhatsapp = settings?.find(s => s.key === 'whatsapp_number')?.value || '';

  const updateSetting = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const existing = settings?.find(s => s.key === key);
      if (existing) {
        await supabase.from('site_settings').update({ value }).eq('key', key);
      } else {
        await supabase.from('site_settings').insert({ key, value });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      toast.success(t('success'));
    },
  });

  return (
    <div className="mt-4 max-w-md">
      <div className="bg-card rounded-xl p-5 border border-border/20">
        <h3 className="font-display text-base font-semibold text-foreground mb-4">{t('settings')}</h3>
        <div>
          <Label className="text-xs">{t('whatsappNumber')}</Label>
          <p className="text-[10px] text-muted-foreground mb-1.5">Current: {currentWhatsapp || 'Not set'}</p>
          <div className="flex gap-2">
            <Input placeholder="+1234567890" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} className="bg-background border-border/30 h-9 text-sm" />
            <Button onClick={() => updateSetting.mutate({ key: 'whatsapp_number', value: whatsapp })} size="sm" className="gradient-accent text-primary-foreground text-xs">{t('save')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
